import React, { useState, useEffect, useMemo } from "react";
import FormMaterial from "./FormMaterial";
import {
  fetchMaterials,
  addMaterial,
  updateMaterial,
  deleteMaterial,
  fetchMaterialFormula,
  saveMaterialFormula,
  removeMaterialFormula,
} from "../../actions/materialAction";
import { connect } from "react-redux";
import { fetchUnits } from "../../actions/unitAction";
import { fetchMaterialCategories } from "../../actions/categoryAction";
import { fetchMaterialCategoryTypes } from "../../actions/categoryTypeAction";
import { AlertConfirmModal } from "../../components/Modals/AlertConfirmModal";
import { sortHelper } from "../../utils/helpers/sortHelper";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";
import UserAvatar from "../../components/UserAvatar";
import { formatIDR } from "../../utils/helpers/formatIDR";
import authorizedAxios from "../../helpers/authorizedAxios";
import { getBaseUnitLabel } from "../../utils/helpers/pricing";
import { toUnitOptions } from "../../utils/helpers/unitOptions";

import MaterialFormulaModal from "./MaterialFormulaModal";

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;

const getPriceTrendMeta = (trend = {}) => {
  if (trend.direction === "up") {
    return {
      className: "bg-danger-lt text-danger",
      icon: "↑",
      label: `Naik ${formatPercent(Math.abs(trend.change_percent || 0))}`,
    };
  }

  if (trend.direction === "down") {
    return {
      className: "bg-success-lt text-success",
      icon: "↓",
      label: `Turun ${formatPercent(Math.abs(trend.change_percent || 0))}`,
    };
  }

  return {
    className: "bg-secondary-lt text-secondary",
    icon: "→",
    label: "Stabil",
  };
};

const formatUnitPrice = (value, unitLabel) => `${formatIDR(value || 0)}/${unitLabel || "unit"}`;

const MaterialPage = (props) => {
  const {
    dataMaterials,
    materials,
    isLoading,
    fetchMaterials,
    addMaterial,
    updateMaterial,
    deleteMaterial,
    fetchMaterialFormula,
    saveMaterialFormula,
    removeMaterialFormula,
    materialFormula,
    isFormulaLoading,
    formulaError,
    fetchUnits,
    units,
    fetchMaterialCategories,
    materialCategories,
    fetchMaterialCategoryTypes,
    materialTypes,
  } = props;
  const unitOptions = useMemo(() => toUnitOptions(units), [units]);

  const [perPage, setPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const initialState = {
    id: "",
    name: "",
    type: "",
    category: "",
    weight: "",
    unit: "",
    unit_cost: 0,
    stock: 0,
    min_stock: 0,
    image: "",
    description: "",
  };
  const [formState, setFormState] = useState(initialState);

  const [isShowModal, setIsShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedPriceMaterial, setSelectedPriceMaterial] = useState(null);
  const [priceHistory, setPriceHistory] = useState(null);
  const [isPriceHistoryLoading, setIsPriceHistoryLoading] = useState(false);
  const [conversionMaterials, setConversionMaterials] = useState([]);
  const [selectedFormulaMaterial, setSelectedFormulaMaterial] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFromUrl = searchParams.get("search") || "";

  const [filterText, setFilterText] = useState(searchFromUrl);

  const { sortConfig, handleSort, getSortedData } = sortHelper.useSort();
  const openModal = () => {
    setIsEdit(false);
    setFormState(initialState);
    setIsShowModal(true);
  };
  const closeModal = () => {
    setFormState(initialState);
    setIsShowModal(false);
    setIsEdit(false);
  };
  const handleDelete = (id, reason) => {
    deleteMaterial(id, reason);
  };
  const handleAdd = async () => {
    const success = await addMaterial(formState);
    if (success) {
      document.querySelector("#modal-form .btn-close")?.click();
    }
    return success;
  };
  const handleUpdate = async (_item) => {
    const success = await updateMaterial(formState?.id, formState);
    if (success) {
      document.querySelector("#modal-form .btn-close")?.click();
    }
    return success;
  };
  const openPriceHistoryModal = async (material) => {
    setSelectedPriceMaterial(material);
    setPriceHistory(null);
    setIsPriceHistoryLoading(true);

    try {
      const response = await authorizedAxios.get(`/materials/${material.id}/price-history`);
      setPriceHistory(response?.data?.data || null);
    } catch (error) {
      setPriceHistory({
        error: error?.response?.data?.message || "Gagal mengambil histori harga material.",
      });
    } finally {
      setIsPriceHistoryLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilterText(value);

    if (value) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    setFilterText(searchFromUrl);
  }, [searchFromUrl]);

  const debouncedFetch = useMemo(
    () =>
      debounce((page, limit, search) => {
        fetchMaterials(limit, page, search);
      }, 500),
    [fetchMaterials],
  );
  useEffect(() => {
    debouncedFetch(currentPage, perPage, searchFromUrl);

    return () => {
      debouncedFetch.cancel();
    };
  }, [currentPage, perPage, searchFromUrl, debouncedFetch]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFromUrl]);
  useEffect(() => {
    fetchUnits(500);
    fetchMaterialCategories(500);
    fetchMaterialCategoryTypes(500);
  }, [fetchUnits, fetchMaterialCategories, fetchMaterialCategoryTypes]);

  useEffect(() => {
    let isMounted = true;

    authorizedAxios
      .get("/materials/conversion-options")
      .then((response) => {
        if (isMounted) setConversionMaterials(response?.data?.data || []);
      })
      .catch(() => {
        if (isMounted) setConversionMaterials(materials || []);
      });

    return () => {
      isMounted = false;
    };
  }, [materials]);

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h1 className="page-title fs-1 text-capitalize">Data Material / Bahan</h1>
              <div className="page-pretitle">master material</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button
                  type="button"
                  className="btn btn-primary btn-5 d-none d-sm-inline-block"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-form"
                  onClick={() => {
                    setFormState(initialState);
                    setIsEdit(false);
                  }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-2">
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                  Tambah material
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-6 d-sm-none btn-icon"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-form"
                  onClick={openModal}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-2">
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          <div className="row row-deck row-cards">
            <div className="col-12">
              <div className="card">
                <div className="card-table">
                  <div className="card-header ">
                    <div className="row w-full">
                      <div className="col-md-9 col-12">
                        <h3 className="card-title mb-0">Material</h3>
                        <p className="text-secondary m-0">Master Bahan</p>
                      </div>
                      <div className="col-md-3 col-12 my-md-0 my-2">
                        <div className="input-group input-group-flat w-auto">
                          {isLoading ? (
                            <span className="input-group-text">
                              <div
                                className="spinner-border spinner-border-sm text-primary text-center"
                                role="status"></div>
                            </span>
                          ) : (
                            <span className="input-group-text">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="icon icon-1">
                                <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                                <path d="M21 21l-6 -6" />
                              </svg>
                            </span>
                          )}

                          <input
                            id="advanced-table-search"
                            type="text"
                            className="form-control"
                            autoComplete="off"
                            onChange={handleSearchChange}
                            placeholder="Cari..."
                            value={filterText}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div id="advanced-table">
                    <div className="table-responsive">
                      <table className="table table-vcenter table-selectable">
                        <thead className="table-header">
                          <tr>
                            <th className="sort-name py-3 w-1">No.</th>
                            <sortHelper.SortableHeader
                              label="Nama"
                              sortKey="name"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />

                            <sortHelper.SortableHeader
                              label="Tipe"
                              sortKey="type"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />

                            <sortHelper.SortableHeader
                              label="Kategori"
                              sortKey="category"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />

                            {/* <th className="sort-status fw-bold">Unit</th> */}
                            {/* <th
                              className="sort-status fw-bold text-center"
                              style={{
                                width: "100px",
                              }}>
                              Isi / Kemasan
                            </th> */}
                            <sortHelper.SortableHeader
                              label="Harga"
                              sortKey="unit_cost"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />

                            <sortHelper.SortableHeader
                              label="Stock"
                              sortKey="stock"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />

                            {/* <th className="sort-status fw-bold">Min Stock</th> */}
                            {/* <th className="sort-status fw-bold">Reseller</th> */}
                            <th
                              className="text-start"
                              style={{
                                width: "25%",
                              }}></th>
                          </tr>
                        </thead>

                        <tbody className="table-tbody">
                          {isLoading
                            ? [...Array(materials?.length || 5)]?.map((_, index) => (
                                <tr
                                  key={`loading-${index}`}
                                  style={{
                                    cursor: "loader",
                                  }}>
                                  <td className="sort-name py-4">{(currentPage - 1) * perPage + index + 1}.</td>
                                  {[...Array(5)].map((_, i) => (
                                    <td key={i}>
                                      <div className="placeholder placeholder-lg w-75"></div>
                                    </td>
                                  ))}
                                  <td className="justify-content-start text-start flex-nowrap">
                                    <div
                                      className="placeholder placeholder-lg me-2 "
                                      style={{
                                        width: "40%",
                                      }}></div>
                                    <div
                                      className="placeholder placeholder-lg "
                                      style={{
                                        width: "40%",
                                      }}></div>
                                  </td>
                                </tr>
                              ))
                            : getSortedData(materials)?.map((Material, index) => (
                                <tr key={Material.id}>
                                  <td className="sort-name">{(currentPage - 1) * perPage + index + 1}.</td>
                                  <td data-label="Nama">
                                    <div className="d-flex py-1 align-items-center">
                                      <UserAvatar
                                        fullName={Material?.name}
                                        imageUrl={Material?.image_path || Material?.image}
                                        image_path={Material?.image_path || Material?.image}
                                        size="md"
                                        className="me-2"
                                        justPhotoView={false}
                                      />

                                      <div className="flex-fill">
                                        <div className="font-weight-medium">{Material?.name}</div>
                                        {Material?.is_virtual_formula ? (
                                          <div className="d-flex align-items-center gap-1 text-secondary small mt-1">
                                            <span className="badge bg-primary-lt text-primary">Formula virtual</span>
                                            <span>{Material?.formula_process_name || "Made to order"}</span>
                                          </div>
                                        ) : Material?.parent_material_name ? (
                                          <div className="text-secondary small mt-1">
                                            Dari {Material.parent_material_name}
                                            {Material?.process_type ? ` · ${Material.process_type}` : ""}
                                            {Material?.default_yield_percent
                                              ? ` · yield ${formatPercent(Material.default_yield_percent)}`
                                              : ""}
                                          </div>
                                        ) : null}
                                        {/* <div className="text-secondary">
                                    <button type="button"  className="text-reset ">
                                    {Material?.type}
                                    </a>
                                    </div> */}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="sort-city">{Material?.type}</td>
                                  <td className="sort-id">
                                    <span className="badge fw-bold">{Material?.category}</span>
                                  </td>
                                  {/* <td className="sort-city">{Material?.unit}</td> */}
                                  {/* <td className="sort-city fw-bold text-center ">
                                    {Number(Material?.weight || 0).toLocaleString("id-ID")}{" "}
                                    {getBaseUnitLabel(unitOptions, Material?.unit)}
                                  </td> */}

                                  <td className="sort-city fw-bold text-start">
                                    {(() => {
                                      const trend = Material?.price_trend || {};
                                      const trendMeta = getPriceTrendMeta(trend);
                                      const currentPrice = trend?.current_price || Material?.unit_cost || 0;
                                      const previousPrice = trend?.previous_price;
                                      const unitLabel = trend?.unit_label || Material?.unit || "unit";
                                      const hasChanged = previousPrice && trend?.direction !== "flat";

                                      return (
                                        <button
                                          type="button"
                                          className="btn border-0 p-0 text-start w-100"
                                          data-bs-toggle="modal"
                                          data-bs-target={Material?.is_virtual_formula ? "#modal-material-formula" : "#modal-material-price-history"}
                                          onClick={() => {
                                            if (Material?.is_virtual_formula) setSelectedFormulaMaterial(Material);
                                            else openPriceHistoryModal(Material);
                                          }}>
                                          <div className="d-flex flex-column align-items-center justify-content-center lh-sm">
                                            {hasChanged ? (
                                              <span className="text-secondary fs-6  text-decoration-line-through">
                                                {formatUnitPrice(previousPrice, unitLabel)}
                                              </span>
                                            ) : null}
                                            <div className="d-flex align-items-center gap-1">
                                              <span className="fw-bold text-center ">
                                                {formatUnitPrice(currentPrice, unitLabel)}
                                              </span>
                                            </div>
                                            <span
                                              className={`fs-6 small fw-bold ${trend?.direction === "up" ? "text-danger" : trend?.direction === "down" ? "text-success" : "text-secondary"}`}>
                                              {Material?.is_virtual_formula ? "HPP formula" : `${trendMeta.icon} ${hasChanged ? trendMeta.label : "Stabil"}`}
                                            </span>
                                          </div>
                                        </button>
                                      );
                                    })()}
                                  </td>
                                  <td className="sort-id text-start">
                                    {/* jikalau stock sama dengan atau kurang dari min_stock maka bg-danger */}
                                    <span
                                      className={`badge ${Number(Material?.stock) === 0 ? "bg-danger-lt" : Material?.stock <= Material?.min_stock ? "bg-warning-lt" : "bg-success-lt"} fw-bold text-white`}>
                                      {Number(Material?.stock || 0).toLocaleString("id-ID")}{" "}
                                      {getBaseUnitLabel(unitOptions, Material?.unit)}
                                    </span>
                                    {Material?.is_virtual_formula ? (
                                      <div className="text-secondary small mt-1">Kapasitas dari bahan</div>
                                    ) : null}
                                  </td>
                                  {/* <td className="sort-status">{Material?.min_stock}</td> */}
                                  <td>
                                    <div className="btn-list flex-nowrap justify-content-start">
                                      <button
                                        type="button"
                                        className="btn btn-1 bg-primary-lt"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modal-material-formula"
                                        aria-label={`Atur formula material ${Material?.name || ""}`.trim()}
                                        onClick={() => setSelectedFormulaMaterial(Material)}>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="icon me-2">
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                          <path d="M4 6h16M4 12h16M4 18h16" />
                                          <path d="M8 3v6M16 9v6M10 15v6" />
                                        </svg>
                                        Formula
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-1 bg-secondary-lt"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modal-form"
                                        aria-label={`Edit material ${Material?.name || ""}`.trim()}
                                        onClick={() => {
                                          setIsEdit(true);
                                          setFormState({
                                            id: Material?.id,
                                            name: Material?.name,
                                            type: Material?.type,
                                            category: Material?.category,
                                            weight: Number(Material?.weight) > 0 ? Material.weight : "",
                                            unit: Material?.unit,
                                            unit_cost: Material?.unit_cost,
                                            stock: Material?.stock,
                                            min_stock: Material?.min_stock,
                                            image: Material?.image,
                                            image_path: Material?.image,
                                            image_url: Material?.image,
                                            description: Material?.description,
                                          });
                                          setIsShowModal(true);
                                        }}>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="icon icon-tabler icons-tabler-outline icon-tabler-edit">
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                          <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                                          <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                                          <path d="M16 5l3 3" />
                                        </svg>{" "}
                                        Edit{" "}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-1 bg-danger-lt"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modal-alert"
                                        aria-label={`Hapus material ${Material?.name || ""}`.trim()}
                                        onClick={() => {
                                          setFormState({
                                            id: Material?.id,
                                            name: Material?.name,
                                            type: Material?.type,
                                            category: Material?.category,
                                            weight: Material?.weight,
                                            unit: Material?.unit,
                                            unit_cost: Material?.unit_cost,
                                            stock: Material?.stock,
                                            min_stock: Material?.min_stock,
                                            image: Material?.image,
                                            description: Material?.description,
                                          });
                                        }}>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="icon icon-tabler icons-tabler-outline icon-tabler-trash">
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                          <path d="M4 7l16 0" />
                                          <path d="M10 11l0 6" />
                                          <path d="M14 11l0 6" />
                                          <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                          <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                                        </svg>{" "}
                                        Hapus
                                      </button>
                                      {/* <Link to={`/testing/print-invoice/${Material?.id}`}>
                                  <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="icon icon-tabler icons-tabler-outline icon-tabler-printer">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                  <path d="M6 17h12c3.5 0 5-1.5 5-5v-5c0-3.5-1.5-5-5-5h-12c-3.5 0-5 1.5-5 5v5c0 3.5 1.5 5 5 5z" />
                                  <path d="M18 17v2c0 2.5-1 3-3 3h-6c-2 0-3-0.5-3-3v-2" />
                                  <path d="M8 11h8" />
                                  <path d="M8 15h6" />
                                  </svg>{" "}
                                  Print
                                  </Link> */}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="card-footer d-flex align-items-center">
                      <div className="dropdown">
                        <button className="btn dropdown-toggle" data-bs-toggle="dropdown">
                          <span id="page-count" className="me-1">
                            {perPage}
                          </span>
                          <span>records</span>
                        </button>
                        <div className="dropdown-menu">
                          {[10, 20, 50, 100]?.map((value) => (
                            <button key={value} className="dropdown-item" onClick={() => handlePerPageChange(value)}>
                              {value} records
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="d-flex ">
                        <p className="ms-2 my-auto text-secondary">
                          {dataMaterials?.per_page} dari <span>{dataMaterials?.total}</span> <span>data</span>
                        </p>
                      </div>

                      <ul className="pagination m-0 ms-auto">
                        {/* PREV BUTTON */}
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>
                            <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                              <path d="M15 6l-6 6l6 6" />
                            </svg>
                          </button>
                        </li>

                        {/* PAGE NUMBERS */}
                        {dataMaterials?.links?.slice(1, -1)?.map((item, index) => {
                          const pageNumber = item.url ? new URL(item.url).searchParams.get("page") : null;

                          return (
                            <li key={index} className={`page-item ${item?.active ? "active" : ""}`}>
                              <button
                                className="page-link"
                                disabled={!pageNumber}
                                onClick={() => pageNumber && setCurrentPage(Number(pageNumber))}>
                                {item.label}
                              </button>
                            </li>
                          );
                        })}

                        {/* NEXT BUTTON */}
                        <li className={`page-item ${currentPage === dataMaterials?.last_page ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(parseInt(dataMaterials?.current_page) + 1)}>
                            <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                              <path d="M9 6l6 6l-6 6" />
                            </svg>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FormMaterial
        isShowModal={isShowModal}
        isEdit={isEdit}
        closeModal={closeModal}
        formState={formState}
        setFormState={setFormState}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        units={units}
        materialCategories={materialCategories}
        materialTypes={materialTypes}
        isLoading={isLoading}
      />

      <MaterialFormulaModal
        material={selectedFormulaMaterial}
        materials={conversionMaterials}
        units={units}
        formula={materialFormula}
        loading={isFormulaLoading}
        error={formulaError}
        fetchFormula={fetchMaterialFormula}
        saveFormula={saveMaterialFormula}
        removeFormula={removeMaterialFormula}
      />

      <MaterialPriceHistoryModal material={selectedPriceMaterial} data={priceHistory} loading={isPriceHistoryLoading} />

      <AlertConfirmModal
        type="error"
        message={`Menghapus data ${formState?.name}`}
        confirmText="Hapus"
        cancelText="Batal"
        idModal="alert"
        idData={formState?.id}
        onConfirm={(id, reason) => {
          handleDelete(id, reason);
        }}
        onCancel={() => {
          // closeModalDelete();
        }}
      />
    </div>
  );
};

const MaterialPriceHistoryModal = ({ material, data, loading }) => {
  const histories = data?.histories || [];
  const summary = data?.summary || material?.price_trend || {};
  const trendMeta = getPriceTrendMeta(summary);

  return (
    <div
      className="modal modal-blur fade"
      id="modal-material-price-history"
      tabIndex="-1"
      role="dialog"
      aria-hidden="true"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
      }}>
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title">Riwayat Harga {material?.name || "Material"}</h5>
              <div className="text-secondary small">
                Pantau perubahan harga beli untuk membaca potensi kenaikan HPP.
              </div>
            </div>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="row g-3">
                {[...Array(3)].map((_, index) => (
                  <div className="col-12" key={`price-history-loading-${index}`}>
                    <div className="placeholder placeholder-lg w-100"></div>
                  </div>
                ))}
              </div>
            ) : data?.error ? (
              <div className="alert alert-danger">{data.error}</div>
            ) : (
              <>
                <div className="card bg-light border-0 mb-3">
                  <div className="card-body">
                    <div className="row align-items-center g-3">
                      <div className="col-md-5">
                        <div className="text-secondary small fw-bold text-uppercase">Purchase Terbaru</div>
                        <div className="h2 mb-0">
                          {formatUnitPrice(
                            summary?.current_price || material?.unit_cost || 0,
                            summary?.unit_label || data?.material?.unit_label,
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-secondary small fw-bold text-uppercase">Purchase Sebelumnya</div>
                        <div className="h3 mb-0">
                          {summary?.previous_price
                            ? formatUnitPrice(summary.previous_price, summary?.unit_label || data?.material?.unit_label)
                            : "-"}
                        </div>
                      </div>
                      <div className="col-md-3 text-md-end">
                        <span className={`badge ${trendMeta.className} fs-4 px-3 py-2`}>
                          <span className="me-1">{trendMeta.icon}</span>
                          {trendMeta.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Tanggal</th>
                        <th>Sumber</th>
                        <th className="text-end">Harga</th>
                        <th className="text-center">Perubahan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {histories.length ? (
                        histories.map((item, index) => {
                          const itemMeta = getPriceTrendMeta(item);

                          return (
                            <tr key={`${item.source}-${item.reference_no}-${index}`}>
                              <td className="fw-bold">{item.price_date}</td>
                              <td>
                                <div className="fw-bold">{item.reference_no}</div>
                                <div className="text-secondary small">{item.supplier_name || item.source}</div>
                              </td>
                              <td className="text-end fw-bold">{formatUnitPrice(item.price, item.unit_label)}</td>
                              <td className="text-center">
                                <span className={`badge ${itemMeta.className}`}>
                                  <span className="me-1">{itemMeta.icon}</span>
                                  {item.previous_price ? itemMeta.label : "Awal"}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-secondary py-5">
                            Belum ada riwayat perubahan harga.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  dataMaterials: state?.materials?.datas,
  materials: state?.materials?.datas?.data || [],
  units: state?.units?.datas?.data || [],
  materialCategories: state?.categories?.materialDatas?.data || [],
  materialTypes: state?.categoryTypes?.materialDatas?.data || [],
  isLoading: state?.materials?.loading,
  materialFormula: state?.materials?.formula,
  isFormulaLoading: state?.materials?.formulaLoading,
  formulaError: state?.materials?.formulaError,
});

export default connect(mapStateToProps, {
  fetchMaterials,
  addMaterial,
  updateMaterial,
  deleteMaterial,
  fetchMaterialFormula,
  saveMaterialFormula,
  removeMaterialFormula,
  fetchUnits,
  fetchMaterialCategories,
  fetchMaterialCategoryTypes,
})(MaterialPage);
